import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Play, Loader2 } from "lucide-react";
import { api } from "@/lib/apiClient";
import { captureRef } from "@/lib/referral";
import { useOrbVoice } from "@/lib/orbVoice";
import AdelaidaPortrait, { framesForVoice } from "@/components/AdelaidaPortrait";
import { XcelioMark } from "@/pages/Gift";
import FoundingBadge from "@/components/partner/FoundingBadge";

// Public page: Adelaida speaks a personal hello to the friend, with his referral code.
export default function Invite() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [failed, setFailed] = useState(false);
  const [state, setState] = useState("ready");
  const speakingRef = useRef(false);
  const { amplitudeRef, speak } = useOrbVoice("sage", "invite");

  useEffect(() => {
    captureRef();
    api.get(`/invite/${token}`).then((r) => setInv(r.data)).catch(() => setFailed(true));
  }, [token]);

  const play = async () => {
    setState("speaking"); speakingRef.current = true;
    api.post(`/invite/${token}/played`).catch(() => {});
    await speak(inv.text, inv.audio);
    speakingRef.current = false; setState("done");
  };

  if (failed) return <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="invite-error"><XcelioMark /><p className="text-muted-foreground">This message isn't available any more.</p><Link to="/" className="text-primary">Discover Xcelio</Link></div>;
  if (!inv) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  return (
    <div className="min-h-screen bg-background flex flex-col items-center px-5 py-8" data-testid="invite-page">
      <XcelioMark />
      <p className="text-[10px] uppercase tracking-[0.3em] text-primary mt-6 mb-4" data-testid="invite-heading">A message for {inv.friend} from {inv.from}'s coach</p>
      {inv.founding && <FoundingBadge className="-mt-2 mb-4" />}
      <div className="relative w-full max-w-sm aspect-[3/4] rounded-3xl overflow-hidden border border-white/10" data-testid="invite-portrait">
        <AdelaidaPortrait amplitudeRef={amplitudeRef} speakingRef={speakingRef} frames={framesForVoice("sage")} status={state === "speaking" ? "speaking" : "idle"} />
        {state === "ready" && (
          <button onClick={play} className="absolute inset-0 flex items-center justify-center bg-black/25" data-testid="invite-play-btn" aria-label="Play message">
            <span className="w-20 h-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center"><Play className="w-7 h-7 ml-1" fill="currentColor" /></span>
          </button>
        )}
      </div>
      <p className="font-serif text-xl italic text-center max-w-sm mt-6 text-foreground/90" data-testid="invite-text">"{inv.text}"</p>
      <Link to={`/register?ref=${inv.code}`} className={`mt-8 px-8 py-3.5 rounded-full font-medium transition-opacity duration-200 ${state === "done" ? "bg-primary text-primary-foreground" : "border border-primary/40 text-primary"}`} data-testid="invite-cta">
        Meet Adelaida, free
      </Link>
      <p className="text-xs text-muted-foreground mt-4 max-w-xs text-center">Xcelio is a personal confidence coach for men. Don't become more impressive. Become more you.</p>
    </div>
  );
}
